import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Backdrop, CircularProgress, Stack, useMediaQuery, useTheme } from '@mui/material';
import { V2PageProvider, useV2Page } from '../../Context/V2PageContext';
import { PageRenderer } from './PageRenderer';
import WelcomeScreenComponent from '../WelcomeScreen/WelcomeScreen';
import { ThankYouScreen } from './ThankYouScreen';
import { BeginInvestigationActionButtons } from '../InvestigationUIActionButtons';
import NavBarComponent from '../NavBar/NavBar';
import ProgressBarComponent from '../ProgressBar';
import FooterComponent from '../Footer/Footer';
import { progressBarConstants } from '@/Config/constantValues';

/**
 * Top-level component for the HRR questionnaire flow.
 *
 * Owns its own `V2PageProvider` so the legacy `InvestigationContext` (which
 * the rest of the questionnaire still consumes globally) is left untouched.
 * On mount we read `sc` from the query string (or accept it via prop from an
 * upstream gate) and call `bootstrap`. The phase machine in the context
 * decides which screen to render.
 *
 * The HRR page is wrapped in the SAME branded shell the legacy questionnaire
 * uses — NavBar (logo + language selector), ProgressBar, Footer (FAQ +
 * "Powered by Refine Intelligence"). The wrapping mirrors `InvestigationUI`'s
 * Stack layout verbatim so the visual identity matches.
 */
export function V2InvestigationPage({ scFromGate }: { scFromGate?: string } = {}) {
	return (
		<V2PageProvider>
			<V2InvestigationFlow scFromGate={scFromGate} />
		</V2PageProvider>
	);
}

function V2InvestigationFlow({ scFromGate }: { scFromGate?: string }) {
	const [searchParams] = useSearchParams();
	const sc = scFromGate ?? searchParams.get('sc') ?? undefined;
	const { phase, currentPage, error, bootstrap, begin } = useV2Page();
	const theme = useTheme();
	const greaterThanSmall = useMediaQuery(theme.breakpoints.up('sm'));

	useEffect(() => {
		if (!sc) return;
		void bootstrap(sc);
	}, [sc, bootstrap]);

	// Map HRR phase → legacy progress-bar percentage. The legacy bar expects
	// 0–100 and snaps to special anchors (welcomeScreen=10, firstQuestionStart=30,
	// questionnaireEnd=100). HRR Phase 1 doesn't yet expose a per-page index so
	// `page`/`submitting` settles at the first-question anchor — better than 0
	// and consistent with how the legacy flow behaves on the very first question.
	const progress = useMemo(() => {
		switch (phase) {
			case 'welcome':
				return progressBarConstants.welcomeScreen;
			case 'page':
			case 'submitting':
				return progressBarConstants.firstQuestionStart;
			case 'done':
				return progressBarConstants.questionnaireEnd;
			default:
				return 0;
		}
	}, [phase]);

	// Hide the progress bar on terminal/loading screens, mirroring legacy
	// `showProgressBar` logic in `InvestigationUI`.
	const showProgressBar = phase === 'welcome' || phase === 'page' || phase === 'submitting';

	// Footer visibility mirrors legacy behavior: always show on >sm screens; on
	// mobile only show during the welcome (beginning) screen.
	const showFooter = greaterThanSmall || phase === 'welcome';

	const renderBody = () => {
		if (!sc) {
			return (
				<Alert severity="error" data-cy="hrr-missing-shortcode">
					This link is missing a short code. Please check your email and try again.
				</Alert>
			);
		}

		if (phase === 'idle' || phase === 'loading') {
			return (
				<Backdrop open sx={{ zIndex: 1000 }} data-cy="hrr-loading">
					<CircularProgress />
				</Backdrop>
			);
		}

		if (phase === 'error') {
			return (
				<Alert severity="error" data-cy="hrr-fatal-error">
					{error?.message ?? 'Something went wrong loading this questionnaire.'}
				</Alert>
			);
		}

		// RND-4283: terminal — the compliance case is closed (409 caseTerminal).
		// The customer must not be able to re-submit; show a dedicated screen.
		if (phase === 'closed') {
			return (
				<Alert severity="info" data-cy="hrr-session-closed">
					This review has been closed and can no longer accept changes. If you believe this is a mistake,
					please contact your bank.
				</Alert>
			);
		}

		// RND-4283: terminal — the link is expired/revoked (boot 401/403).
		if (phase === 'expired') {
			return (
				<Alert severity="warning" data-cy="hrr-expired">
					This link has expired or is no longer valid. Please check your email for the most recent link, or
					contact your bank.
				</Alert>
			);
		}

		if (phase === 'welcome') {
			// Reuse the exact same WelcomeScreenComponent as v1 — only the questions renderer differs.
			// Begin button is rendered in the bottom action area (see layout below).
			return <WelcomeScreenComponent handleOnClickNext={() => void begin()} />;
		}

		if (phase === 'done') {
			return <ThankYouScreen />;
		}

		// phase === 'page' | 'submitting'
		if (!currentPage) {
			return (
				<Backdrop open sx={{ zIndex: 1000 }} data-cy="hrr-loading">
					<CircularProgress />
				</Backdrop>
			);
		}
		// `key` by pageId so React remounts PageRenderer on every page change.
		// Without it the same instance is reused and its local `submitted` flag
		// leaks across pages — making each new page's required fields render in
		// an error state on arrival, before the user has interacted (RND-4336).
		return <PageRenderer key={currentPage.pageId} page={currentPage} />;
	};

	return (
		<Stack width={'100%'} height={`100svh`} data-cy="hrr-investigation-shell">
			{/* RND-4274: HRR PQ is not localized (no Spanish content yet) — hide the language toggle. */}
			<NavBarComponent showLanguageSelector={false} isQuestionTrailDisplayed={false} />
			<Stack
				paddingBottom={'8px'}
				paddingInline={{
					xxs: '24px',
					xs: '24px',
					sm: '48px',
					md: '105px',
					lg: '350px',
					xl: '403px',
				}}
				paddingTop={{
					xxs: '16px',
					xs: '16px',
					sm: '32px',
					md: '32px',
					lg: '32px',
					xl: '32px',
				}}
				height={'100%'}
				overflow={'scroll'}>
				<Stack gap={'24px'} justifyContent={'space-between'} height={'100%'} overflow={'scroll'}>
					{renderBody()}
					<Stack gap={'24px'}>
						{phase === 'welcome' && (
							<BeginInvestigationActionButtons
								beginButtonText="Begin"
								handleOnClickBeginning={() => void begin()}
							/>
						)}
						{showProgressBar && <ProgressBarComponent progressPercentage={progress} />}
					</Stack>
				</Stack>
			</Stack>
			{showFooter && <FooterComponent />}
		</Stack>
	);
}
